import { QSET_GENERATOR_RESULTS_DATA_TEST_ID } from '@e2e/constants/exam-management';
import { NavigationBlocker } from '@exam-management/components';
import {
  useCreateQSetsByGeneratorExecutionMutation,
  useGetQSetGeneratorExecutionByIdQuery,
} from '@exam-management/graphql';
import { Navigate, useNavigate, useParams } from '@exam-management/router';
import { Button, Flex, Loading, Paper, Text } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { HelperBanner, SummaryStatsGroup } from '../../components';
import {
  type QSetGeneratorResultColumn,
  PassedResultsTable,
} from '../../components/passed-results-table';
import type { QSetGeneratorResultStatistic } from '../../types';
import { ResultsErrorState } from '../results-error-state';

const ROW_PER_PAGE = 10;

export default function QSetGenerateResult() {
  const { showSnackbar } = useSnackbar();
  const { state } = useLocation();
  const navigate = useNavigate();

  const { courseId } = useParams('/exam-courses/:courseId/qsets/generator/results');
  const [selectedQSets, setSelectedQSets] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const isCreateQSetsSuccessful = useRef(false);

  const { data: GetQSetGeneratorExecutionResult, loading: isGetQSetGeneratorExecutionLoading } =
    useGetQSetGeneratorExecutionByIdQuery({
      variables: {
        input: {
          id: state?.qSetGeneratorId,
        },
      },
      skip: !state,
      onCompleted: () => {
        window.history.replaceState({}, '', `/exam-courses/${courseId}/qsets/generator/results`);
      },
    });

  const [createQSets, { loading: isQSetsCreating }] = useCreateQSetsByGeneratorExecutionMutation({
    onCompleted: (data) => {
      isCreateQSetsSuccessful.current = true;
      showSnackbar({
        variant: 'success',
        message: `${data.excCreateQSetsByGeneratorExecution.qSets.length} QSets Added to Draft`,
      });
      navigate('/exam-courses/:courseId/qsets', { replace: true, params: { courseId } });
    },
    onError: () => {
      showSnackbar({
        variant: 'error',
        message: 'Unable to create selected QSets',
      });
    },
  });

  const handleConfirm = async () => {
    await createQSets({
      variables: {
        input: {
          id: state?.qSetGeneratorId,
          resultIds: selectedQSets,
        },
      },
    });
  };

  const { rows, stats } = useMemo(() => {
    const qSetGeneratorExecution =
      GetQSetGeneratorExecutionResult?.excGetQSetGeneratorExecutionById?.qSetGeneratorExecution;

    const {
      duplicationRate = 0,
      totalQuestions = 0,
      needTotal = 0,
      qConfig = {
        id: '',
        name: '',
        code: 0,
      },
    } = qSetGeneratorExecution?.generator ?? {};

    const passedResults =
      qSetGeneratorExecution?.results?.filter((result) => result.criteriaResult === 'PASSED') ?? [];

    const rows: QSetGeneratorResultColumn[] = passedResults
      .map((result) => {
        return {
          id: result.id,
          code: result.code,
          averageDuplicationRate: result.averageDuplicationRate,
          totalQuestions,
          qConfig,
        };
      })
      .sort((a, b) => a.code.localeCompare(b.code));

    const stats: QSetGeneratorResultStatistic = {
      totalQuestions,
      duplicationRate,
      results: {
        total: needTotal,
        totalPassed: passedResults.length,
      },
    };

    return {
      rows,
      stats,
    };
  }, [GetQSetGeneratorExecutionResult]);

  const {
    results: { totalPassed },
  } = stats;

  const isAllFailed = totalPassed === 0;
  const selectedCount = selectedQSets.length;

  if (!state && !isGetQSetGeneratorExecutionLoading) {
    showSnackbar({
      variant: 'warning',
      message: 'ไม่พบข้อมูลผลลัพธ์การสร้างชุดข้อสอบ',
    });
    return <Navigate to='/exam-courses/:courseId/qsets' params={{ courseId }} />;
  }

  if (isGetQSetGeneratorExecutionLoading) {
    return (
      <Loading
        sx={{
          height: '100%',
        }}
      />
    );
  }

  return (
    <Paper sx={{ width: '100%', flex: 1 }}>
      <Flex
        sx={(theme) => ({
          flexDirection: 'column',
          gap: theme.spacing(6),
          p: theme.spacing(6),
          height: '100%',
        })}
      >
        <SummaryStatsGroup data={stats} />
        {isAllFailed ? (
          <ResultsErrorState />
        ) : (
          <>
            <HelperBanner data={stats.results} />
            <Flex
              sx={(theme) => ({
                borderRadius: theme.shape.rounded(2),
                border: '1px solid',
                borderColor: theme.palette.grey[300],
                overflow: 'hidden',
                flexDirection: 'column',
                flex: 1,
              })}
            >
              <Flex
                sx={(theme) => ({
                  p: theme.spacing(4),
                  justifyContent: 'space-between',
                  alignItems: 'center',
                })}
              >
                <Text variant='h4'>ชุดข้อสอบที่ผ่านเกณฑ์</Text>
                <Button
                  data-testid={QSET_GENERATOR_RESULTS_DATA_TEST_ID.CONFIRM_SELECTED_BUTTON}
                  disabled={selectedCount === 0}
                  onClick={handleConfirm}
                  loading={isQSetsCreating}
                >
                  Confirm Selected ({selectedCount})
                </Button>
              </Flex>
              <PassedResultsTable
                slotProps={{
                  footer: {
                    total: totalPassed,
                    pagination: {
                      page,
                      count: totalPassed,
                      rowsPerPage: 10,
                      rowsPerPageOptions: [],
                      onPageChange: (_event, page) => setPage(page),
                    },
                  },
                }}
                paginationModel={{
                  page,
                  pageSize: ROW_PER_PAGE,
                }}
                pageSizeOptions={[]}
                rows={rows}
                rowSelectionModel={selectedQSets}
                onRowSelectionModelChange={(newRowSelectionModel) => {
                  setSelectedQSets(newRowSelectionModel.map(String));
                }}
              />
            </Flex>
          </>
        )}
      </Flex>
      <NavigationBlocker
        shouldBlock={() => !isCreateQSetsSuccessful.current}
        slotProps={{
          confirmationModal: {
            content:
              'คุณแน่ใจหรือไม่ที่จะออกจากหน้านี้โดยที่ไม่เลือกชุดข้อสอบเพื่อนำเข้า Draft ซึ่งชุดข้อสอบทั้งหมดที่ถูกสร้างจะหายไป',
          },
        }}
      />
    </Paper>
  );
}
